import { brands, utils } from '@huggg/back-bar';
import { is, path, pathOr, prop, propOr } from 'ramda';
import { translateAPIError } from 'utilities/errorHandling';
import { getOperator } from 'utilities/recipientBranding';
import initialStateLoader from '../initialState';

export const UPDATE_BRAND = 'huggg/brand/UPDATE';
const LOAD_HOWTO = 'huggg/brand/howTo/LOAD';
const LOAD_HOWTO_SUCCESS = 'huggg/brand/howTo/LOAD_SUCCESS';
const LOAD_HOWTO_FAILURE = 'huggg/brand/howTo/LOAD_FAILURE';
const LOAD_TERMS = 'huggg/brand/terms/LOAD';
const LOAD_TERMS_SUCCESS = 'huggg/brand/terms/LOAD_SUCCESS';
const LOAD_TERMS_FAILURE = 'huggg/brand/terms/LOAD_FAILURE';
const LOAD_LOCATIONS = 'huggg/brand/locations/LOAD';
const LOAD_LOCATIONS_SUCCESS = 'huggg/brand/locations/LOAD_SUCCESS';
const LOAD_LOCATIONS_FAILURE = 'huggg/brand/locations/LOAD_FAILURE';

const getDefaultState = () => {
  const initialState = initialStateLoader();
  return {
    brand: initialState.brand,
    terms: {
      content: null,
      loading: false,
      error: null,
    },
    howTo: {
      content: null,
      loading: false,
      error: null,
    },
    locations: {
      content: propOr(null, 'locations', initialState),
      loading: false,
      error: null,
    },
  };
};

/**
 * Triggered when something like a huggg or a superhuggg is updated.
 */
const externalUpdate = (state, payload) => {
  const brand = payload.brand;
  const locations = {
    ...state.locations,
    content: brand.locations || state.locations.content,
  };

  return { ...state, brand, locations };
};

const reducer = (state = {}, { type, payload }) => {
  state = { ...getDefaultState(), ...state };
  switch (type) {
    case UPDATE_BRAND:
      return externalUpdate(state, payload);

    case LOAD_HOWTO_SUCCESS:
      return { ...state, howTo: { ...payload, error: null, loading: false } };

    case LOAD_HOWTO:
      return { ...state, howTo: { loading: true, error: null, content: null } };

    case LOAD_HOWTO_FAILURE:
      return { ...state, howTo: { ...payload, content: null, loading: false } };

    case LOAD_TERMS_SUCCESS:
      return { ...state, terms: { ...payload, error: null, loading: false } };

    case LOAD_TERMS:
      return { ...state, terms: { loading: true, error: null, content: null } };

    case LOAD_TERMS_FAILURE:
      return { ...state, terms: { ...payload, content: null, loading: false } };

    case LOAD_LOCATIONS_SUCCESS:
      return {
        ...state,
        locations: { ...payload, error: null, loading: false },
      };

    case LOAD_LOCATIONS:
      return {
        ...state,
        locations: { loading: true, error: null, content: null },
      };

    case LOAD_LOCATIONS_FAILURE:
      return {
        ...state,
        locations: { ...payload, content: null, loading: false },
      };

    default:
      return state;
  }
};

export default reducer;

/**
 * SIDE EFFECTS
 */
const throttled = {};
const fetchThrottled = (content, id, apiMethod) => {
  const key = content + id;
  throttled[key] = throttled[key]
    ? throttled[key]
    : utils.throttlePromise(() => apiMethod(id));

  return throttled[key];
};

export const fetchHowTo = () => (dispatch, getState) => {
  const id = getBrand(getState()).id;
  if (!id) {
    dispatch({
      type: LOAD_HOWTO_FAILURE,
      payload: { error: 'Brand not found.' },
    });
  }
  dispatch({ type: LOAD_HOWTO, payload: {} });

  const doFetch = fetchThrottled('howTo', id, brands.howTo);
  return doFetch()
    .then((data) => {
      dispatch({
        type: LOAD_HOWTO_SUCCESS,
        payload: { content: data },
      });
    })
    .catch((err) => {
      dispatch({
        type: LOAD_HOWTO_FAILURE,
        payload: {
          error: translateAPIError(err, getOperator(getState().huggg?.huggg)),
        },
      });
    });
};

export const fetchTerms = () => (dispatch, getState) => {
  const id = getBrand(getState()).id;
  dispatch({ type: LOAD_TERMS, payload: {} });

  const doFetch = fetchThrottled('terms', id, brands.terms);
  return doFetch()
    .then((data) => {
      dispatch({
        type: LOAD_TERMS_SUCCESS,
        payload: { content: data },
      });
    })
    .catch((err) => {
      dispatch({
        type: LOAD_TERMS_FAILURE,
        payload: {
          error: translateAPIError(err, getOperator(getState().huggg?.huggg)),
        },
      });
    });
};

export const fetchLocations = () => (dispatch, getState) => {
  const id = getBrand(getState()).id;
  dispatch({ type: LOAD_LOCATIONS, payload: {} });

  const doFetch = fetchThrottled('locations', id, (id) =>
    brands.byId([id], { embed: ['stores'] }),
  );
  return doFetch()
    .then(({ data: brands, embedded }) => {
      if (is(Array, brands) && brands.length === 1) {
        dispatch({
          type: LOAD_LOCATIONS_SUCCESS,
          payload: { content: prop('stores', embedded) },
        });
      } else {
        dispatch({
          type: LOAD_LOCATIONS_FAILURE,
          payload: { error: 'No locations found' },
        });
      }
    })
    .catch((err) => {
      dispatch({
        type: LOAD_LOCATIONS_FAILURE,
        payload: {
          error: translateAPIError(err, getOperator(getState().huggg?.huggg)),
        },
      });
    });
};
/**
 * ACTIONS
 */

/**
 * SELECTORS
 */
export const getBrand = (state) => path(['brand', 'brand'], state);

export const getHowToError = (state) =>
  path(['brand', 'howTo', 'error'], state);

export const getHowTo = (state) => path(['brand', 'howTo', 'content'], state);

export const getTerms = (state) => path(['brand', 'terms', 'content'], state);

export const getTermsError = (state) =>
  path(['brand', 'terms', 'error'], state);

export const getLocations = (state) =>
  pathOr([], ['brand', 'locations', 'content'], state);

export const getLocationsError = (state) =>
  path(['brand', 'locations', 'error'], state);
